import { defineStore } from 'pinia'
import type { AuthResponse } from '@/types/auth'
import { useQuoteStore } from './quote'
import { useShipmentStore } from './shipment'
import { useCustomerStore } from './customer'
import { useTransactionStore } from './transaction'
import { authApi } from '@/api/auth'
import api from '../api/api'
import type { User } from '@/types/user'
import router from '@/router'
interface AuthState {
  token: string | null
  tokenExpiration: number | null
  user: User | null
  refreshTokenTimer: any | null
}

// 从 localStorage 获取初始状态
const getStoredAuth = (): AuthState => {
  const storedToken = localStorage.getItem('auth_token')
  const storedExpiration = localStorage.getItem('auth_token_expiration')
  const storedUser = localStorage.getItem('auth_user')

  return {
    token: storedToken,
    tokenExpiration: storedExpiration ? Number(storedExpiration) : null,
    user: storedUser ? JSON.parse(storedUser) : null,
    refreshTokenTimer: null,
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => getStoredAuth(),

  getters: {
    isAuthenticated: state => !!state.token,
    isTokenExpired: state => {
      if (!state.tokenExpiration) return true
      return Date.now() / 1000 >= state.tokenExpiration
    },
    // isSystemUser: state => state.user?.from_uid === 'system', //todo
  },

  actions: {
    // 设置 token
    setToken(token: string, expiration: number) {
      this.token = token
      this.tokenExpiration = expiration

      // 保存到 localStorage
      localStorage.setItem('auth_token', token)
      localStorage.setItem('auth_token_expiration', expiration.toString())
    },

    // 设置用户信息
    setUserInfo(user: User) {
      this.user = user
      localStorage.setItem('auth_user', JSON.stringify(user))
    },
    // 设置用户信息
    setInviteCode(inviteCode: string) {
      if (this.user) {
        this.user.inviteCode = inviteCode
        localStorage.setItem('auth_user', JSON.stringify(this.user))
      }
    },

    setMenus(menus: Array<any>) {
      localStorage.setItem('auth_menus', JSON.stringify(menus))
    },
    // 完整的设置认证信息（为了向后兼容）
    setAuth(token: string, user: User) {
      // this.setToken(token, user.tokenExpiration)
      this.setUserInfo(user)
    },

    clearAuth() {
      // 停止 token 刷新定时器
      this.stopRefreshTokenTimer()

      // 清除认证相关数据
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_token_expiration')
      localStorage.removeItem('auth_user')
      // localStorage.removeItem('invite_code')
      localStorage.removeItem('auth_menus')

      // 清除客户数据
      // const customerStore = useCustomerStore()
      // customerStore.clearCustomers()

      // 清除交易记录
      const transactionStore = useTransactionStore()
      transactionStore.clearTransactions()

      // 清除运输数据
      const shipmentStore = useShipmentStore()
      shipmentStore.clearForm()

      // 清除报价数据
      const quoteStore = useQuoteStore()
      quoteStore.clearQuotes()

      // 重置 store 状态
      this.token = null
      this.tokenExpiration = null
      this.user = null
    },

    updateBalance(newBalance: number) {
      if (this.user) {
        this.user.userMoney = newBalance
      }
    },

    async refreshToken() {
      try {
        const response = await authApi.refreshToken()
        console.log('Refresh token response:', response)
        if (response.code === 0 && response.data) {
          const { token, expires_at } = response.data
          this.setToken(token, expires_at)
          // 更新 axios 默认 headers
          api.defaults.headers.common['token'] = token
          return true
        }
        return false
      } catch (error) {
        console.error('Failed to refresh token:', error)
        return false
      }
    },

    startRefreshTokenTimer() {
      // 清除可能存在的旧定时器
      this.stopRefreshTokenTimer()

      // 设置新的定时器，每分钟刷新一次
      this.refreshTokenTimer = setInterval(async () => {
        const success = await this.refreshToken()
        if (!success) {
          this.stopRefreshTokenTimer()
          this.clearAuth()
        }
      }, 60000 * 30)
    },

    stopRefreshTokenTimer() {
      if (this.refreshTokenTimer) {
        clearInterval(this.refreshTokenTimer)
        this.refreshTokenTimer = null
      }
    },
  },
})
